import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Role definitions
export type UserRole = 'admin' | 'manager' | 'user' | 'viewer';

export interface Role {
  id: string;
  name: string;
  level: UserRole;
  permissions: Permission[];
  description: string;
}

export interface Permission {
  id: string;
  name: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'execute';
  scope?: 'own' | 'team' | 'all';
}

export interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: Role[];
  avatar?: string;
  department?: string;
  lastLogin?: Date;
  isActive: boolean;
  preferences: {
    theme: 'light' | 'dark' | 'system';
    language: 'fr' | 'en';
    dashboard: {
      layout: 'grid' | 'list';
      widgets: string[];
    };
    notifications: {
      email: boolean;
      push: boolean;
      desktop: boolean;
    };
  };
}

interface UserRoleContextType {
  user: User | null;
  isLoading: boolean;
  hasPermission: (resource: string, action: string, scope?: string) => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  canAccess: (module: string) => boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  switchRole: (roleId: string) => void;
  currentRole: Role | null;
  isAdmin: boolean;
  isManager: boolean;
  loading: boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

// Mock permissions data
const PERMISSIONS: Permission[] = [
  // Dashboard permissions
  { id: 'dashboard_view', name: 'View Dashboard', resource: 'dashboard', action: 'read' },
  { id: 'dashboard_customize', name: 'Customize Dashboard', resource: 'dashboard', action: 'update', scope: 'own' },
  
  // PRI+ permissions
  { id: 'pri_view', name: 'View PRI Calculations', resource: 'pri', action: 'read' },
  { id: 'pri_create', name: 'Create PRI Calculations', resource: 'pri', action: 'create' },
  { id: 'pri_edit', name: 'Edit PRI Calculations', resource: 'pri', action: 'update' },
  { id: 'pri_delete', name: 'Delete PRI Calculations', resource: 'pri', action: 'delete' },
  { id: 'pri_approve', name: 'Approve PRI Calculations', resource: 'pri', action: 'execute' },
  
  // PRI Sheets permissions
  { id: 'pri_sheets_view', name: 'View PRI Sheets', resource: 'pri_sheets', action: 'read' },
  { id: 'pri_sheets_create', name: 'Create PRI Sheets', resource: 'pri_sheets', action: 'create' },
  { id: 'pri_sheets_edit', name: 'Edit PRI Sheets', resource: 'pri_sheets', action: 'update' },
  { id: 'pri_sheets_delete', name: 'Delete PRI Sheets', resource: 'pri_sheets', action: 'delete' },
  
  // User management permissions
  { id: 'users_view', name: 'View Users', resource: 'users', action: 'read' },
  { id: 'users_create', name: 'Create Users', resource: 'users', action: 'create' },
  { id: 'users_edit', name: 'Edit Users', resource: 'users', action: 'update' },
  { id: 'users_delete', name: 'Delete Users', resource: 'users', action: 'delete' },
  
  // System permissions
  { id: 'system_settings', name: 'System Settings', resource: 'system', action: 'update' },
  { id: 'system_logs', name: 'View System Logs', resource: 'system', action: 'read' },
  { id: 'system_backup', name: 'System Backup', resource: 'system', action: 'execute' },
];

// Mock roles data
const ROLES: Role[] = [
  {
    id: 'admin',
    name: 'Administrateur',
    level: 'admin',
    description: 'Accès complet au système',
    permissions: PERMISSIONS // Admin has all permissions
  },
  {
    id: 'manager',
    name: 'Manager',
    level: 'manager',
    description: 'Gestion d\'équipe et approbations',
    permissions: PERMISSIONS.filter(p => 
      !['users_create', 'users_delete', 'system_settings', 'system_backup'].includes(p.id)
    )
  },
  {
    id: 'user',
    name: 'Utilisateur',
    level: 'user',
    description: 'Utilisation standard des modules',
    permissions: PERMISSIONS.filter(p => 
      ['dashboard_view', 'dashboard_customize', 'pri_view', 'pri_create', 'pri_edit', 
       'pri_sheets_view', 'pri_sheets_create', 'pri_sheets_edit'].includes(p.id)
    )
  },
  {
    id: 'viewer',
    name: 'Observateur',
    level: 'viewer',
    description: 'Accès en lecture seule',
    permissions: PERMISSIONS.filter(p => 
      ['dashboard_view', 'pri_view', 'pri_sheets_view'].includes(p.id)
    )
  }
];

// Mock user data
const MOCK_USER: User = {
  id: 'user-1',
  name: 'Marie Dubois',
  firstName: 'Marie',
  lastName: 'Dubois',
  email: 'marie.dubois@plvplus.com',
  roles: [ROLES[2]], // Admin role by default
  avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
  department: 'Direction',
  lastLogin: new Date(),
  isActive: true,
  preferences: {
    theme: 'light',
    language: 'fr',
    dashboard: {
      layout: 'grid',
      widgets: ['pri-overview', 'recent-calculations', 'system-stats']
    },
    notifications: {
      email: true,
      push: true,
      desktop: false
    }
  }
};

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading user data
    const loadUser = async () => {
      setIsLoading(true);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUser(MOCK_USER);
      setCurrentRole(MOCK_USER.roles[0]);
      setIsLoading(false);
    };

    loadUser();
  }, []);

  const hasPermission = (resource: string, action: string, scope?: string): boolean => {
    if (!user || !currentRole) return false;
    
    return currentRole.permissions.some(permission => 
      permission.resource === resource && 
      permission.action === action &&
      (!scope || !permission.scope || permission.scope === scope)
    );
  };

  const hasRole = (role: UserRole): boolean => {
    if (!user) return false;
    return user.roles.some(userRole => userRole.level === role);
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return user.roles.some(userRole => roles.includes(userRole.level));
  };

  const canAccess = (module: string): boolean => {
    if (!user) return false;
    
    switch (module) {
      case 'dashboard':
        return hasPermission('dashboard', 'read');
      case 'pri-plus':
        return hasPermission('pri', 'read');
      case 'users':
        return hasPermission('users', 'read');
      case 'settings':
        return hasRole('admin') || hasRole('manager');
      default:
        return false;
    }
  };

  const login = async (_credentials: { email: string; password: string }): Promise<void> => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock successful login
    setUser(MOCK_USER);
    setCurrentRole(MOCK_USER.roles[0]);
    setIsLoading(false);
  };

  const logout = (): void => {
    setUser(null);
    setCurrentRole(null);
  };

  const switchRole = (roleId: string): void => {
    if (!user) return;
    
    const role = user.roles.find(r => r.id === roleId);
    if (role) {
      setCurrentRole(role);
    }
  };

  const contextValue: UserRoleContextType = {
    user,
    isLoading,
    hasPermission,
    hasRole,
    hasAnyRole,
    canAccess,
    login,
    logout,
    switchRole,
    currentRole,
    isAdmin: hasRole('admin'),
    isManager: hasRole('manager'),
    loading: isLoading
  };

  return (
    <UserRoleContext.Provider value={contextValue}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRoles = (): UserRoleContextType => {
  const context = useContext(UserRoleContext);
  if (context === undefined) {
    throw new Error('useUserRoles must be used within a UserRoleProvider');
  }
  return context;
};

// Helper hook for role-based rendering
export const useRoleGuard = () => {
  const { hasRole, hasPermission, canAccess } = useUserRoles();
  
  return {
    hasRole,
    hasPermission,
    canAccess,
    isAdmin: () => hasRole('admin'),
    isManager: () => hasRole('manager'),
    isUser: () => hasRole('user'),
    isViewer: () => hasRole('viewer'),
    canManageUsers: () => hasPermission('users', 'create') || hasPermission('users', 'update'),
    canViewSystemLogs: () => hasPermission('system', 'read'),
    canCreatePRI: () => hasPermission('pri', 'create'),
    canApprovePRI: () => hasPermission('pri', 'execute')
  };
};

export default useUserRoles;
